/**
 * ============================================================================
 * Specifikit UI JS
 * Location: assets/specifikit-ui.js
 * Version: 1.0
 * Author: specifikit.com
 * ============================================================================
 */

class SpecifikitUI {
  constructor(coreInstance) {
    this.core = coreInstance;
    this.container = coreInstance.container;
    this.logger = coreInstance.logger; // Use the logger from the core instance
    this.elements = {};
    this.modal = {
      instance: null,
      trigger: null,
      isOpen: false,
    };

    this.themeWishlistButton = {
      element: null,
      originalParent: null,
      originalNextSibling: null,
      isCaptured: false
    };

    this.isDropdownHandlerAttached = false;
  }

  queryElements() {
    this.elements = {
      form: this.container.querySelector('[data-specifikit-form]'),
      submitButton: this.container.querySelector('[data-specifikit-submit-button], .product-form__submit'),
      submitPrice: this.container.querySelector('[data-specifikit-submit-price]'),
      mainImage: this.container.querySelector('[data-specifikit-image-simple]'),
      imageLayerStack: this.container.querySelector('[data-specifikit-image-layer-stack]'),
      imageContainer: this.container.querySelector('[data-specifikit-image-container]'),
      imageSpinner: this.container.querySelector('[data-specifikit-image-spinner]'),
      imageError: this.container.querySelector('[data-specifikit-image-error]'),
      summaryList: this.container.querySelector('[data-specifikit-summary-list]'),
      cartError: this.container.querySelector('[data-specifikit-cart-error]'),
      variantIdInput: this.container.querySelector('[data-specifikit-variant-id-input]'),
      sizeOptions: this.container.querySelectorAll('[data-specifikit-option-input="size"]'),
      componentOptions: {},
      addonInputs: this.container.querySelectorAll('[data-specifikit-addon-input]'),
      quantityInput: this.container.querySelector('[data-specifikit-quantity-input], input[name="quantity"]'),
      productInfoContainer: this.container.querySelector('.specifikit-product-info'),
      productPriceContainer: this.container.querySelector('.specifikit-price-container'),
      actionPanel: this.container.querySelector('.specifikit-action-panel'),
      actionButtonsWrapper: this.container.querySelector('.specifikit-action-buttons-wrapper'),
      notificationsContainer: this.container.querySelector('[data-specifikit-notifications-container]'),
      uiContainer: this.container.querySelector('[data-specifikit-ui-container]'),
      uiShell: this.container.querySelector('[data-specifikit-ui-shell]'),
      mainGrid: this.container.querySelector('[data-specifikit-main-grid]'),
      galleryBar: this.container.querySelector('[data-specifikit-gallery-bar]'),
      resumeButton: this.container.querySelector('[data-specifikit-resume-button]'),
      loaderContainer: this.container.querySelector('[data-specifikit-loader-container]'),
      canvasBaseImage: this.container.querySelector('[data-specifikit-canvas-base]'),
      viewToolbar: this.container.querySelector('[data-specifikit-view-toolbar]')
    };

    const sizeComponent = this.core.config.components?.find(c => c.key === 'size' && c.enabled);
    if (sizeComponent) {
      this.elements.componentOptions.size = this.container.querySelectorAll('[data-specifikit-option-input="size"]');
    }

    this.core.config.components?.forEach(comp => {
      if (comp.enabled && comp.key !== 'size') {
        this.elements.componentOptions[comp.key] = this.container.querySelectorAll(`[data-specifikit-option-input="${comp.key}"]`);
      }
    });
  }

  validateElements() {
    const critical = ['mainGrid', 'productInfoContainer'];
    critical.forEach(k => {
      if (!this.elements[k]) this.logger.error(`Missing critical element for validation: ${k}`);
    });

    const sizeComponent = this.core.config.components?.find(c => c.key === 'size' && c.enabled);
    if (sizeComponent && (!this.elements.componentOptions.size || this.elements.componentOptions.size.length === 0)) {
      this.logger.error('Size component enabled but no size options rendered. Check size_options metafield.');
    }

    this.core.config.components?.forEach(comp => {
      if (comp.enabled && (!this.elements.componentOptions[comp.key] || this.elements.componentOptions[comp.key].length === 0)) {
        this.logger.error(`${comp.label}: No options rendered`);
      }
    });
  }

  setMode(newMode) {
    const core = this.core;
    if (core.mode === newMode && this.elements.imageContainer?.dataset.specifikitMode) return;
    core.mode = newMode;
    this.logger.info(`🖼️ Specifikit Mode: ${core.mode}`);

    if (!this.elements.imageContainer) return;

    this.elements.imageContainer.dataset.specifikitMode = core.mode;

    const showBorderInCustomizer = this.core.config.galleryConfig.border_in_customizer_mode;
    const showBorderInGallery = this.core.config.galleryConfig.border_in_gallery_mode;
    const shouldShowBorder = (core.mode === 'customizer' && showBorderInCustomizer) || (core.mode === 'gallery' && showBorderInGallery);
    this.elements.imageContainer.dataset.borderEnabled = shouldShowBorder;

    const infoBarEnabled = this.core.config.galleryConfig.info_bar_enabled;
    const hideInfoBarOnMobile = this.core.config.galleryConfig.info_bar_hide_on_mobile;
    const shouldShowInfoBar = core.mode === 'gallery' && infoBarEnabled;
    this.elements.imageContainer.dataset.infoBarEnabled = shouldShowInfoBar;
    this.elements.imageContainer.dataset.infoBarHideMobile = hideInfoBarOnMobile;

    this.elements.imageContainer.style.cursor = (core.mode === 'gallery') ? 'default' : '';
  }

  async enhanceProductPage() {
    this.logger.info('✨ Enhancing page with theme-agnostic features...');
    await this.transplantThemeContent();

    if (this.core.config.enableTransplantation) {
      this.applyAdaptiveImageSizing();
      this.makeDescriptionCollapsible();
    }
  }

  async transplantThemeContent() {
    this.logger.groupCollapsed('🌱 Transplanting theme content...');
    try {
      const sourceUrl = `${window.location.pathname}?view=none`;
      const response = await fetch(sourceUrl);
      if (!response.ok) throw new Error(`Network response not OK for transplant source: ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const mainProductSourceNode = doc.querySelector('section[id*="__main"], [id^="MainProduct-"], .product-template, main#main-content, .product-section') || doc.body;

      const movedElements = new Set();

      const canTransplant = (element) => {
        if (!element) return false;
        if (element.closest('.specifikit-transplanted-source')) return false;
        if (movedElements.has(element)) return false;
        return true;
      };

      const markAsTransplanted = (element) => {
        element.classList.add('specifikit-transplanted-source');
        movedElements.add(element);
      };

      const transplant = (selectors, placeholderKey, options = {}) => {
        const {
          position = 'append', clear = true, searchNode = mainProductSourceNode
        } = options;
        const destination = this.container.querySelector(`[data-specifikit-placeholder="${placeholderKey}"]`);
        if (!destination) return;

        if (clear) destination.innerHTML = '';

        for (const selector of selectors) {
          const elements = searchNode.querySelectorAll(selector);
          elements.forEach(element => {
            if (canTransplant(element)) {
              const clone = element.cloneNode(true);
              clone.classList.add('specifikit-transplanted');

              if (position === 'append') {
                destination.appendChild(clone);
              } else if (position === 'prepend') {
                destination.prepend(clone);
              }

              markAsTransplanted(element);
              this.logger.log(`✅ Transplanted '${selector}' to '${placeholderKey}'`);
            }
          });
        }
      };

      const allTargets = {
        presentational: [{
          key: 'vendor',
          selectors: ['.product__vendor', '.product-meta__vendor', '[class*="--vendor"]', '.product__text.inline-richtext:not(:has(a))']
        }, {
          key: 'title',
          selectors: ['h1.product__title', 'h1.product-single__title', '.product__title > h1', '.product-title', 'h1[itemprop="name"]']
        }, {
          key: 'price',
          selectors: ['[id^="price-"]', '.product__price', '.product-price:not(.price--end)', '.price:not(.price--end)', '[data-price-wrapper]']
        }, {
          key: 'description',
          selectors: ['.product__description', '.product-description', '.product-single__description', '[class*="description"].rte']
        }, {
          key: 'share',
          selectors: ['share-button', '.share-button', '[class*="share-buttons"]']
        }, {
          key: 'thumbnails',
          selectors: ['slider-component.thumbnail-slider', '.product__thumbs', '.product-thumbnails', '[class*="thumbnail-list"]']
        }, ],
        functional: [{
          key: 'payment-buttons',
          selectors: ['[data-shopify="payment-button"]', '.shopify-payment-button']
        }]
      };

      this.logger.log('⚙️ Transplanting functional theme content (always runs)...');
      allTargets.functional.forEach(target => transplant(target.selectors, target.key, target.options || {}));

      if (this.core.config.enableTransplantation) {
        this.logger.log('🌱 Transplanting presentational theme content...');
        allTargets.presentational.forEach(target => transplant(target.selectors, target.key, target.options || {}));

        const transplantedPrice = this.container.querySelector('[data-specifikit-placeholder="price"] .specifikit-transplanted');
        if (transplantedPrice) {
          transplantedPrice.style.display = 'none';
          transplantedPrice.setAttribute('aria-hidden', 'true');
        }

        await this.transplantRelatedProducts(doc);

        const transplantedThumbnails = this.container.querySelector('[data-specifikit-placeholder="thumbnails"] .specifikit-transplanted');
        if (transplantedThumbnails) {
          this.integrateTransplantedThumbnails(transplantedThumbnails);
        } else {
          const placeholder = this.container.querySelector('[data-specifikit-placeholder="thumbnails"]');
          if (placeholder) placeholder.style.display = 'none';
        }

      } else {
        this.logger.log('🚫 Skipping presentational theme content transplantation as per admin setting.');
      }

      // START: Share Button Integration
      const transplantedShareButton = this.container.querySelector('[data-specifikit-placeholder="share"] .specifikit-transplanted.share-button');
      if (transplantedShareButton) {
        const shareSummary = transplantedShareButton.querySelector('summary');
        if (shareSummary) {
          // Prevent the theme's default JS and the <details> toggle
          shareSummary.addEventListener('click', (e) => {
            e.preventDefault();
            
            const shareData = {
              title: this.core.config.productTitle,
              text: `Check out this product: ${this.core.config.productTitle}`,
              url: window.location.origin + window.location.pathname
            };
      
            if (navigator.share && navigator.canShare(shareData)) {
              // Use the modern Web Share API
              navigator.share(shareData)
                .then(() => this.logger.log('Product shared successfully via Web Share API.'))
                .catch((error) => this.logger.error('Web Share API failed.', error));
            } else {
              // Fallback: Manually open our beautiful modal from advanced features
              const advancedShareHandler = window.advancedFeatures?.shareConfiguration.bind(window.advancedFeatures);
              if (typeof advancedShareHandler === 'function') {
                this.logger.log('Share: Using advanced feature share modal as a fallback.');
                advancedShareHandler();
              } else {
                // Absolute failsafe: just toggle the <details> element
                this.logger.warn('Share: Advanced share modal not available. Using styled theme fallback.');
                const detailsElement = transplantedShareButton.querySelector('details');
                if (detailsElement) {
                  detailsElement.open = !detailsElement.open;
                }
              }
            }
          });
      
          // Also handle the fallback's copy button ourselves to ensure it works
          const copyButton = transplantedShareButton.querySelector('.share-button__copy');
          const urlInput = transplantedShareButton.querySelector('.share-button__fallback input');
          const message = transplantedShareButton.querySelector('.share-button__message');
      
          if (copyButton && urlInput && message) {
            copyButton.addEventListener('click', () => {
              urlInput.select();
              navigator.clipboard.writeText(urlInput.value).then(() => {
                message.textContent = 'Link copied!'; // This could be internationalized
                message.classList.remove('hidden');
                setTimeout(() => message.classList.add('hidden'), 2000);
              }).catch(err => {
                this.logger.error('Failed to copy text: ', err);
              });
            });
          }
          this.logger.info('✅ Re-wired transplanted share button for robust, multi-layered functionality.');
        }
      }
      // END: Share Button Integration

      const topAppsPlaceholder = this.container.querySelector('[data-specifikit-placeholder="app-blocks-top"]');
      const bottomAppsPlaceholder = this.container.querySelector('[data-specifikit-placeholder="app-blocks-bottom"]');

      if (topAppsPlaceholder && bottomAppsPlaceholder) {
        const allAppBlockSelectors = ['shopify-payment-terms', '.installment', '.product__tax', '[id*="afterpay"]', 'square-placement', '.shopify-app-block'];
        mainProductSourceNode.querySelectorAll(allAppBlockSelectors.join(', ')).forEach(block => {
          if (canTransplant(block)) {
            const isNearTop = block.closest('.product__info-container, .product__info-wrapper')?.querySelector('[id^="price-"], h1, .product__price, .product-title');
            const destination = isNearTop ? topAppsPlaceholder : bottomAppsPlaceholder;
            const clone = block.cloneNode(true);
            clone.classList.add('specifikit-transplanted');
            destination.appendChild(clone);
            markAsTransplanted(block);
          }
        });
        this.logger.log(`✅ Scanned for and relocated App blocks and payment terms.`);
      }

      const originalForm = mainProductSourceNode.querySelector('form[action*="/cart/add"], product-form');
      const formContainer = this.container.querySelector('[data-specifikit-placeholder="form-container"]');
      if (originalForm && formContainer && canTransplant(originalForm)) {
        const formClone = originalForm.cloneNode(true);
        formContainer.appendChild(formClone);
        formClone.classList.add('specifikit-transplanted', 'specifikit-original-form-source');
        markAsTransplanted(originalForm);
        this.logger.log('✅ Cloned original product form for maximum app compatibility.');

        const formInputsPlaceholder = this.container.querySelector(`[data-specifikit-placeholder="form-inputs"]`);
        if (formInputsPlaceholder) {
          const atcButton = formClone.querySelector('[name="add"], .product-form__submit');
          if (atcButton) {
            formInputsPlaceholder.appendChild(atcButton.cloneNode(true));
          }
        }
      }

      this.container.querySelectorAll('.specifikit-transplanted script').forEach(script => {
        const newScript = document.createElement('script');
        for (const {
            name,
            value
          } of script.attributes) newScript.setAttribute(name, value);
        if (script.innerHTML) newScript.appendChild(document.createTextNode(script.innerHTML));
        script.parentNode.replaceChild(newScript, script);
      });
      this.logger.log('🔄 Re-executed scripts within transplanted content.');

      this.logger.info('🌱 Transplant complete.');
      this.logger.groupEnd();
    } catch (error) {
      this.logger.error('Failed to transplant theme content:', error);
      this.logger.groupEnd();
    }
  }

  async transplantRelatedProducts(sourceDoc) {
    const destination = this.container.querySelector('[data-specifikit-placeholder="related-products"]');
    if (!destination) {
      this.logger.log('No related products placeholder found in template.');
      return;
    }

    try {
      let relatedSection = null;

      const highConfidenceSelectors = [
        'product-recommendations',
        '.product-recommendations',
        '#product-recommendations',
        '[data-section-type*="recommendations"]',
        '.related-products',
        '#related-products',
        '[data-section-type*="related-products"]',
        '.product-related'
      ];

      for (const selector of highConfidenceSelectors) {
        const element = sourceDoc.querySelector(selector);
        if (element) {
          relatedSection = element.closest('.shopify-section') || element;
          this.logger.log(`[Related Products] Found via Strategy 1 (High-Confidence): '${selector}'`);
          break;
        }
      }

      if (!relatedSection) {
        const searchTerms = ["you may also like", "related products", "complete the look", "frequently bought together"];
        const allSections = sourceDoc.querySelectorAll('.shopify-section');

        for (const section of allSections) {
          const sectionText = section.innerText.toLowerCase();
          if (searchTerms.some(term => sectionText.includes(term))) {
            if (section.querySelector('[class*="card-product"], .grid-product, .product-item, .product-card')) {
              relatedSection = section;
              this.logger.log(`[Related Products] Found via Strategy 2 (Heuristic Content Search)`);
              break;
            }
          }
        }
      }

      if (!relatedSection) {
        const broadSelectors = [
          '[id*="related-products"]',
          '[class*="related-products"]',
          '[id*="recommendations"]',
          '[class*="recommendations"]',
          '[id*="upsell"]',
          '[class*="upsell"]'
        ];

        for (const selector of broadSelectors) {
          const element = sourceDoc.querySelector(selector);
          if (element) {
            relatedSection = element.closest('.shopify-section') || element;
            this.logger.log(`[Related Products] Found via Strategy 3 (Broad Fallback): '${selector}'`);
            break;
          }
        }
      }

      if (relatedSection) {
        this.logger.log(`✅ Transplanting related products section ('${relatedSection.id || relatedSection.className}')...`);
        destination.innerHTML = '';
        const clone = relatedSection.cloneNode(true);
        clone.classList.add('specifikit-transplanted');
        destination.appendChild(clone);
        destination.style.display = '';

        clone.querySelectorAll('script').forEach(script => {
          const newScript = document.createElement('script');
          for (const {
              name,
              value
            } of script.attributes) newScript.setAttribute(name, value);
          if (script.innerHTML) newScript.appendChild(document.createTextNode(script.innerHTML));
          script.parentNode.replaceChild(newScript, script);
        });

      } else {
        this.logger.log('No related products section found to transplant. Hiding placeholder.');
        destination.style.display = 'none';
      }
    } catch (error) {
      this.logger.error('Error during related products transplant:', error);
      destination.style.display = 'none';
    }
  }

  _displayGalleryImage(imageUrl) {
    this.showSpinner(true);
    this.showImageError(false);

    let imageElement = this.elements.mainImage;
    if (!imageElement && this.elements.imageLayerStack) {
      this.elements.imageLayerStack.innerHTML = '';
      const newImg = document.createElement('img');
      newImg.id = 'specifikit-dynamic-product-image';
      newImg.dataset.specifikitImageSimple = '';
      newImg.setAttribute('alt', this.core.config?.productTitle || 'Product image');
      newImg.loading = 'eager';
      this.elements.imageLayerStack.appendChild(newImg);

      this.elements.mainImage = newImg;
      imageElement = newImg;
    } else if (!imageElement) {
      this.logger.error('No image container found to display the gallery image.');
      this.showSpinner(false);
      return;
    }

    imageElement.style.opacity = '0';
    imageElement.style.transition = 'opacity 0.3s ease';

    const loadHandler = () => {
      imageElement.style.opacity = '1';
      this.showSpinner(false);
      this.showImageError(false);
    };

    const errorHandler = () => {
      this.logger.error(`Failed to load gallery image: ${imageUrl}`);
      this.showImageError(true, 'Could not load image');
      this.showSpinner(false);
    };

    imageElement.onload = loadHandler;
    imageElement.onerror = errorHandler;

    imageElement.src = imageUrl;

    if (imageElement.complete && imageElement.naturalWidth > 0) {
      Promise.resolve().then(loadHandler);
    }
  }

  integrateTransplantedThumbnails(thumbnailContainer) {
    this.logger.info('🔗 Integrating transplanted thumbnails (Gallery Engine)...');
    this.elements.transplantedThumbnails = thumbnailContainer;
    const thumbnails = thumbnailContainer.querySelectorAll('button.thumbnail, a.thumbnail, [role="button"], .thumbnail-list__item, .product__thumb-item');

    const allThumbs = thumbnails.length > 0 ? thumbnails : thumbnailContainer.querySelectorAll('li');
    if (allThumbs.length === 0) {
      this.logger.warn("Could not find any selectable thumbnail elements to integrate.");
      return;
    }

    const hasActiveThumb = thumbnailContainer.querySelector('[aria-current="true"], .is-active, .active');

    allThumbs.forEach((thumb, index) => {
      thumb.classList.add('specifikit-thumbnail-item');

      if (index === 0 && !hasActiveThumb) {
        thumb.setAttribute('aria-current', 'true');
      }

      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // =================================================================
        // Clicking a thumbnail is a user interaction that must exit the
        // initial view state. This ensures "Resume Customizing" works.
        // =================================================================
        if (this.core.state.isInitialState) {
          this.core.state.isInitialState = false;
          this.logger.log('🚪 Exiting initial state due to gallery interaction.');
        }

        this.setMode('gallery');
        
        // Deactivate 360 view if it's active
        if (window.advancedFeatures && typeof window.advancedFeatures.deactivate360View === 'function') {
            window.advancedFeatures.deactivate360View(true); // Pass true to force deactivation
        }

        // Deactivate the unified view toolbar
        const specifikitViewButtons = document.querySelectorAll('.specifikit-view-button, .specifikit-view-360-button');
        specifikitViewButtons.forEach(btn => btn.classList.remove('specifikit-active'));
        if (this.elements.viewToolbar) {
            this.elements.viewToolbar.classList.remove('specifikit-view-active');
        }

        // Delegate state change to Core class
        this.core.clearViewStateForGallery();

        const highResSrc = this.findHighResImageFromThumbnail(thumb);
        if (!highResSrc) {
          this.logger.warn('Could not determine high-res image from thumbnail:', thumb);
          return;
        }

        this._displayGalleryImage(highResSrc);

        allThumbs.forEach(t => t.removeAttribute('aria-current'));
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  findHighResImageFromThumbnail(thumbnailElement) {
    if (thumbnailElement.dataset.fullUrl) return thumbnailElement.dataset.fullUrl;

    const link = thumbnailElement.closest('a');
    if (link && link.href && (link.href.includes('.jpg') || link.href.includes('.png') || link.href.includes('.webp'))) {
      return link.href.replace(/_\d+[x@]_?/g, '');
    }

    const targetId = thumbnailElement.dataset.target || thumbnailElement.getAttribute('aria-controls');
    if (targetId) {
      const mainSlideImg = document.querySelector(`#${targetId} img, [data-media-id="${targetId}"] img`);
      if (mainSlideImg) return mainSlideImg.src.replace(/_\d+[x@]_?/g, '');
    }

    const img = thumbnailElement.querySelector('img');
    if (img && img.srcset) {
      const sources = img.srcset.split(',').map(s => {
        const parts = s.trim().split(/\s+/);
        return {
          url: parts[0],
          width: parseInt(parts[1], 10) || 0
        };
      });
      if (sources.length > 0) return sources.sort((a, b) => b.width - a.width)[0].url;
    }

    return img ? img.src.replace(/_\d+[x@]_?/g, '') : null;
  }

  applyAdaptiveImageSizing() {
    const initialImage = this.elements.mainImage;
    if (initialImage) {
      initialImage.onload = () => this.updateImageContainerAspectRatio(initialImage);
      if (initialImage.complete && initialImage.naturalHeight > 0) {
        this.updateImageContainerAspectRatio(initialImage);
      }
    }
  }

  updateImageContainerAspectRatio(imageElement) {
    if (this.core.config.imageConfig.mode === 'advanced' || this.core.config.imageConfig.mode === 'canvas') {
      this.elements.imageContainer.style.aspectRatio = '';
      return;
    }
    if (imageElement && imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      if (this.elements.imageContainer) {
        this.elements.imageContainer.style.aspectRatio = `${imageElement.naturalWidth} / ${imageElement.naturalHeight}`;
      }
    }
  }

  makeDescriptionCollapsible() {
    const descriptionPlaceholder = this.container.querySelector('[data-specifikit-placeholder="description"]');
    if (!descriptionPlaceholder) return;

    const getEffectiveBackgroundColor = (el) => {
      let color;
      let currentEl = el;
      while (currentEl && currentEl !== document.body) {
        color = window.getComputedStyle(currentEl).backgroundColor;
        if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
          return color;
        }
        currentEl = currentEl.parentElement;
      }
      return 'rgb(255, 255, 255)';
    };

    const processDescription = (description) => {
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          const realHeight = entry.target.scrollHeight;
          const MAX_HEIGHT = 220;
          if (realHeight > MAX_HEIGHT && !entry.target.classList.contains('is-collapsible-initialized')) {
            entry.target.classList.add('is-collapsible-initialized', 'is-collapsed');
            entry.target.style.maxHeight = `${MAX_HEIGHT}px`;

            const readMoreBtn = document.createElement('button');
            readMoreBtn.className = 'specifikit-read-more-btn';
            readMoreBtn.textContent = 'Read More';
            readMoreBtn.setAttribute('aria-expanded', 'false');
            entry.target.insertAdjacentElement('afterend', readMoreBtn);

            readMoreBtn.addEventListener('click', () => {
              const isCollapsed = entry.target.classList.toggle('is-collapsed');
              entry.target.style.maxHeight = isCollapsed ? `${MAX_HEIGHT}px` : `${realHeight}px`;
              readMoreBtn.textContent = isCollapsed ? 'Read More' : 'Read Less';
              readMoreBtn.setAttribute('aria-expanded', String(!isCollapsed));
            });

            const containerBg = getEffectiveBackgroundColor(description.parentElement);
            const rgbValues = containerBg.match(/\d+/g)?.slice(0, 3).join(', ') || '255, 255, 255';
            const styleId = 'specifikit-collapsible-styles';

            let style = document.getElementById(styleId);
            if (!style) {
              style = document.createElement('style');
              style.id = styleId;
              document.head.appendChild(style);
            }
            style.textContent = `
                    .specifikit-product-description.is-collapsed::after, .specifikit-transplanted[class*="description"].is-collapsed::after {
                      background: linear-gradient(to top, rgb(${rgbValues}) 20%, rgba(${rgbValues}, 0));
                    }
                  `;
            observer.disconnect();
          }
        }
      });
      observer.observe(description);
    };

    const initialDescription = descriptionPlaceholder.querySelector('.product__description, .product-description, .rte');
    if (initialDescription) {
      processDescription(initialDescription);
    } else {
      const mutationObserver = new MutationObserver((mutations) => {
        for (let mutation of mutations) {
          if (mutation.addedNodes.length > 0) {
            const addedDescription = descriptionPlaceholder.querySelector('.product__description, .product-description, .rte');
            if (addedDescription) {
              processDescription(addedDescription);
              mutationObserver.disconnect();
              break;
            }
          }
        }
      });
      mutationObserver.observe(descriptionPlaceholder, {
        childList: true,
        subtree: true
      });
    }
  }

  setupModalMode() {
    this.queryElements();
    this.logger.info('✨ Applying modal display mode');
    if (!this.elements.uiShell) {
      this.logger.error("UI Shell for modal not found.");
      return;
    }

    this.modal.trigger = document.createElement('button');
    this.modal.trigger.className = 'specifikit-btn specifikit-btn-lg specifikit-btn-block specifikit-btn-open-modal';
    this.modal.trigger.textContent = this.core.config.modalButtonText || 'Customize Product';
    this.modal.trigger.setAttribute('aria-haspopup', 'dialog');

    this.elements.uiShell.appendChild(this.modal.trigger);

    if (this.elements.uiContainer) {
      this.elements.uiContainer.style.display = 'none';
    }

    this.modal.trigger.addEventListener('click', () => this.openModal());
  }

  async openModal() {
    if (this.modal.isOpen) return;

    document.body.classList.add('specifikit-modal-open');

    if (!this.modal.instance) {
      this.createModal();
    }

    const modalControls = this.modal.instance.querySelector('[data-specifikit-modal-controls]');
    if (modalControls) {
      if (this.elements.productPriceContainer) { // Use the main container, not just the wrapper
        modalControls.prepend(this.elements.productPriceContainer);
      }
      modalControls.appendChild(this.elements.uiContainer);
      this.elements.uiContainer.style.display = 'block';
    }

    if (this.themeWishlistButton.element) {
      const formInModal = this.modal.instance.querySelector('[data-specifikit-form]');
      if (formInModal) {
        formInModal.appendChild(this.themeWishlistButton.element);
        this.logger.log('✅ Moved theme wishlist button into modal.');
      } else {
        this.logger.warn('Could not find form in modal to append wishlist button.');
      }
    }

    this.modal.instance.classList.add('is-visible');
    this.modal.isOpen = true;

    this.container.dispatchEvent(new CustomEvent('specifikit:modalOpened', {
      bubbles: true
    }));

    this.core.updateUI();
  }

  closeModal() {
    if (!this.modal.instance || !this.modal.isOpen) return;

    this.modal.instance.classList.remove('is-visible');
    document.body.classList.remove('specifikit-modal-open');
    this.modal.isOpen = false;

    this.container.dispatchEvent(new CustomEvent('specifikit:modalClosed', {
      bubbles: true
    }));

    const {
      element,
      originalParent,
      originalNextSibling
    } = this.themeWishlistButton;
    if (element && originalParent) {
      try {
        originalParent.insertBefore(element, originalNextSibling);
        this.logger.log('✅ Restored theme wishlist button to its original position.');
      } catch (e) {
        this.logger.error('Could not restore wishlist button. Original parent might be gone. Appending to product info container as a failsafe.', e);
        this.elements.productInfoContainer?.appendChild(element);
      }
    }

    if (this.elements.productInfoContainer && this.elements.productPriceContainer) {
        // Insert the price container back before the UI shell.
        this.elements.productInfoContainer.insertBefore(this.elements.productPriceContainer, this.elements.uiShell);
    }
    this.elements.uiShell.appendChild(this.elements.uiContainer);
    this.elements.uiContainer.style.display = 'none';

    this.core.onVisualStateChange();
    this.syncMainPagePriceOnModalClose();
  }

  syncMainPagePriceOnModalClose() {
    this.updatePrice(); // Re-use the main updatePrice function to sync both displays
  }

  createModal() {
    const template = document.getElementById('specifikit-modal-view-template');
    if (!template) {
      this.logger.error("Modal template not found. Modal cannot be created.");
      return;
    }

    const modalFragment = template.content.cloneNode(true);
    this.modal.instance = modalFragment.querySelector('.specifikit-modal-view');

    if (!this.modal.instance) {
      this.logger.error("Invalid modal template structure.");
      return;
    }

    this.modal.instance.dataset.sectionId = this.container.dataset.sectionId;
    this.modal.instance.classList.add('specifikit-product-customizer-wrapper');
    this.modal.instance.dataset.typographicIsland = this.container.dataset.typographicIsland;

    document.body.appendChild(this.modal.instance);

    const modalImagesContainer = this.modal.instance.querySelector('[data-specifikit-modal-images]');
    const modalControlsContainer = this.modal.instance.querySelector('[data-specifikit-modal-controls]');

    if (!modalImagesContainer || !modalControlsContainer) {
      this.logger.error("Modal template is missing critical placeholder elements.");
      return;
    }

    const imageContainerToClone = this.container.querySelector('.specifikit-main-product-image');

    if (imageContainerToClone) {
      const clonedImageContainer = imageContainerToClone.cloneNode(true);
      modalImagesContainer.appendChild(clonedImageContainer);

      // Re-query the toolbar in the new modal scope and re-attach listeners
      const clonedToolbar = clonedImageContainer.querySelector('[data-specifikit-view-toolbar]');
      if (clonedToolbar) {
        this.setupUnifiedViewToolbar(clonedToolbar); // Re-run setup for the cloned toolbar
      }
    }

    if (this.elements.productPriceContainer) {
      modalControlsContainer.prepend(this.elements.productPriceContainer);
    }
    modalControlsContainer.appendChild(this.elements.uiContainer);
    this.elements.uiContainer.style.display = 'block';

    this.modal.instance.querySelector('.specifikit-modal-overlay').addEventListener('click', () => this.closeModal());
    this.modal.instance.querySelector('.specifikit-modal-close').addEventListener('click', () => this.closeModal());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.isOpen) {
        this.closeModal();
      }
    });

    this.container.dispatchEvent(new CustomEvent('specifikit:modalReady', {
      bubbles: true,
      detail: {
        modalElement: this.modal.instance
      }
    }));
  }

  /**
   * ========================================================================
   * START: setupUnifiedViewToolbar (RE-ARCHITECTED)
   * This master function orchestrates the entire view toolbar, ensuring
   * perfect synchrony, theming, and support for all view-related features.
   * It now clones buttons from pre-built HTML templates for maximum
   * stability and customizability.
   * ========================================================================
   */
  setupUnifiedViewToolbar(scope = this.container) {
    const viewToolbar = scope.querySelector('[data-specifikit-view-toolbar]');
    if (!viewToolbar) {
      this.logger.warn('Unified View Toolbar (`[data-specifikit-view-toolbar]`) not found. Cannot render view controls.');
      return;
    }
    viewToolbar.innerHTML = ''; // Clear any existing buttons

    const imageConfig = this.core.config.imageConfig;
    const advancedFeatures = window.advancedFeatures;
    let hasButtons = false;

    // 1. Render Multi-Angle View Buttons
    if (imageConfig?.views && Array.isArray(imageConfig.views) && imageConfig.views.length > 0) {
        const defaultView = imageConfig.views.find(v => v.default === true) || imageConfig.views[0];
        
        imageConfig.views.forEach(view => {
            const btn = this._createViewButton(view);
            if (view.handle === defaultView.handle) {
                btn.classList.add('specifikit-active');
            }
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.core.handleViewChange(view.handle, true);
            });
            viewToolbar.appendChild(btn);
            hasButtons = true;
        });
    }

    // 2. Render Advanced Feature Buttons (if advanced module is loaded)
    if (advancedFeatures) {
      // 2a. 360° View Button
      if (advancedFeatures.featureFlags.enable_360_view && this.core.config.threeSixtyViewConfig?.enable) {
          const btn = this._createAdvancedViewButton('360');
          if (btn) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                advancedFeatures.toggle360View();
            });
            viewToolbar.appendChild(btn);
            hasButtons = true;
          }
      }

      // 2b. AR Preview Button
      const isARCapable = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (advancedFeatures.featureFlags.enable_ar_preview && this.core.config.arConfig?.enable && isARCapable) {
          const btn = this._createAdvancedViewButton('ar');
          if (btn) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                advancedFeatures.launchARPreview();
            });
            viewToolbar.appendChild(btn);
            hasButtons = true;
          }
      }

      // 2c. Image Zoom Button
      if (advancedFeatures.featureFlags.enable_image_zoom) {
          const btn = this._createAdvancedViewButton('zoom');
          if (btn) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                advancedFeatures.openLightbox();
            });
            viewToolbar.appendChild(btn);
            hasButtons = true;
          }
      }
    }
    
    // =================================================================
    // START: UX IMPROVEMENT FOR INITIAL VIEW
    // Hide the entire toolbar if the initial view is active, as the
    // controls are not relevant to it.
    // =================================================================
    if (this.core.state.isInitialState) {
        viewToolbar.style.display = 'none';
    } else if (!hasButtons) {
        viewToolbar.style.display = 'none';
    }
    // =================================================================
    // END: UX IMPROVEMENT
    // =================================================================

    this.logger.info('✅ Unified View Toolbar populated successfully.');
  }

  _createViewButton(view) {
      const btn = document.createElement('button');
      btn.className = 'specifikit-btn specifikit-view-button';
      btn.dataset.view = view.handle;
      
      const labelSpan = `<span>${this.escapeHtml(view.label)}</span>`;
      
      // Intelligent Icon Rendering
      if (view.icon && typeof view.icon === 'string') {
          btn.innerHTML = `${view.icon}${view.label ? labelSpan : ''}`;
          btn.classList.add('has-icon');
          if (!view.label) {
            btn.classList.add('specifikit-view-button--icon-only');
            btn.setAttribute('aria-label', this.escapeHtml(view.handle));
          }
      } else {
          btn.innerHTML = labelSpan;
      }
      return btn;
  }

  /**
   * Clones a pre-built, themed advanced feature button from the Liquid template.
   * This is the method for creating themeable buttons.
   * @param {string} type - The type of button to create ('360', 'ar', 'zoom').
   * @returns {HTMLElement|null} The cloned button element or null if not found.
   */
  _createAdvancedViewButton(type) {
      const templateContainer = document.getElementById('specifikit-view-button-templates');
      if (!templateContainer) {
          this.logger.error(`View Toolbar: Button template container not found.`);
          return null;
      }
      const buttonTemplate = templateContainer.content.querySelector(`[data-template-button="${type}"]`);
      if (!buttonTemplate) {
          this.logger.error(`View Toolbar: Template for button type "${type}" not found.`);
          return null;
      }
      return buttonTemplate.cloneNode(true);
  }

  handleViewChangeUI(viewHandle) {
    // This selector is now global to ensure it updates buttons in both the main page AND modal contexts simultaneously.
    document.querySelectorAll('.specifikit-view-button[data-view]').forEach(btn => {
      btn.classList.toggle('specifikit-active', btn.dataset.view === viewHandle);
    });

    // Deactivate theme thumbnails to show we are in customizer view mode
    if (this.elements.transplantedThumbnails) {
      this.elements.transplantedThumbnails.classList.add('specifikit-view-active');
      const activeThumbs = this.elements.transplantedThumbnails.querySelectorAll('[aria-current="true"], .is-active, .active');
      activeThumbs.forEach(thumb => {
        thumb.removeAttribute('aria-current');
        thumb.classList.remove('is-active', 'active');
      });
    }
  }

  // Deactivates all static multi-angle view buttons
  deactivateAllViewButtons() {
    document.querySelectorAll('.specifikit-view-button[data-view]').forEach(btn => {
        btn.classList.remove('specifikit-active');
    });
  }

  // Reactivates the button corresponding to the current core state
  reactivateCurrentViewButton() {
      const currentView = this.core.state.view;
      if (currentView) {
          document.querySelectorAll(`.specifikit-view-button[data-view="${currentView}"]`).forEach(btn => {
              btn.classList.add('specifikit-active');
          });
      }
  }

  _attachGlobalDropdownHandler() {
    if (this.isDropdownHandlerAttached) return;

    document.addEventListener('click', (e) => {
      const openDropdown = document.querySelector('.specifikit-component-dropdown.is-open');
      if (openDropdown && !openDropdown.contains(e.target)) {
        const selectedDisplay = openDropdown.querySelector('.specifikit-component-dropdown__trigger');
        const optionGroup = openDropdown.closest('.specifikit-option-group');
        const accordionItem = openDropdown.closest('.specifikit-accordion-item');

        openDropdown.classList.remove('is-open');
        if (selectedDisplay) selectedDisplay.setAttribute('aria-expanded', 'false');
        if (optionGroup) optionGroup.classList.remove('dropdown-is-active');
        if (accordionItem) accordionItem.classList.remove('child-dropdown-open');
      }
    });
    
    this.isDropdownHandlerAttached = true;
    this.logger.log('💧 Robust global dropdown handler attached.');
  }

  setupComponentDisplayStyles() {
    let hasDropdown = false;
    this.core.config.components?.forEach(comp => {
      if (comp.enabled && comp.display_style === 'dropdown') {
        hasDropdown = true;
        const optionsContainer = this.container.querySelector(`[data-specifikit-option-list="${comp.key}"]`);
        if (optionsContainer) {
          this.createCustomDropdown(comp, optionsContainer);
        }
      }
    });

    if (hasDropdown) {
      this._attachGlobalDropdownHandler();
    }
  }

  updateCustomDropdownDisplay(dropdownWrapper, value) {
    const selectedDisplay = dropdownWrapper.querySelector('.specifikit-component-dropdown__trigger');
    const optionsList = dropdownWrapper.querySelector('.specifikit-component-dropdown__list');
    const group = dropdownWrapper.closest('.specifikit-option-group');
    if (!group || !selectedDisplay || !optionsList) return;

    const componentKey = group.dataset.specifikitOptionGroup;
    const options = this.core.config.options[componentKey] || [];
    const selectedOptionConfig = options.find(o => o.handle === value);

    if (!selectedOptionConfig) return;

    // Structure content for perfect consistency ---
    const contentWrapper = document.createElement('span');
    contentWrapper.className = 'specifikit-dropdown-content';

    const swatch = this.createSwatchElement(selectedOptionConfig);
    if (swatch) {
      contentWrapper.appendChild(swatch);
    }

    // Use the consistent class name for the text label
    const text = document.createElement('span');
    text.className = 'specifikit-component-dropdown__option-text';
    text.textContent = selectedOptionConfig.label;
    contentWrapper.appendChild(text);

    if (selectedOptionConfig.price_modifier > 0) {
      const price = document.createElement('span');
      price.className = 'specifikit-price-mod';
      price.textContent = `(+${this.core.formatMoney(selectedOptionConfig.price_modifier * 100)})`;
      contentWrapper.appendChild(price);
    }
    
    const arrowIcon = document.createElement('span');
    arrowIcon.className = 'specifikit-dropdown-arrow';
    arrowIcon.setAttribute('aria-hidden', 'true');
    arrowIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 8l4 4 4-4"/></svg>`;

    // Clear the button and append new, structured content safely
    while (selectedDisplay.firstChild) {
      selectedDisplay.removeChild(selectedDisplay.firstChild);
    }
    selectedDisplay.appendChild(contentWrapper);
    selectedDisplay.appendChild(arrowIcon);

    optionsList.querySelectorAll('.specifikit-component-dropdown__option').forEach(opt => {
      const isSelected = opt.dataset.value === value;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', String(isSelected));
    });
  }
  
  /**
   * Creates a fully accessible, keyboard-navigable custom dropdown.
   * @param {object} component - The component configuration object.
   * @param {HTMLElement} optionsContainer - The original container for the hidden radio inputs.
   */
  createCustomDropdown(component, optionsContainer) {
    this.logger.log(`✨ Creating world-class dropdown for ${component.key}`);
    optionsContainer.style.display = 'none';

    const dropdownWrapper = document.createElement('div');
    dropdownWrapper.className = 'specifikit-dropdown-wrapper specifikit-component-dropdown';

    const groupLabel = optionsContainer.closest('.specifikit-option-group')?.querySelector('.specifikit-option-label');
    const groupLabelId = `specifikit-dropdown-label-${component.key}`;
    if (groupLabel) groupLabel.id = groupLabelId;

    const selectedDisplay = document.createElement('button');
    selectedDisplay.className = 'specifikit-dropdown-selected specifikit-component-dropdown__trigger';
    selectedDisplay.type = 'button';
    selectedDisplay.setAttribute('aria-haspopup', 'listbox');
    selectedDisplay.setAttribute('aria-expanded', 'false');
    if(groupLabel) selectedDisplay.setAttribute('aria-labelledby', groupLabelId);

    const optionsList = document.createElement('div');
    optionsList.className = 'specifikit-dropdown-options specifikit-component-dropdown__list';
    optionsList.setAttribute('role', 'listbox');
    if(groupLabel) optionsList.setAttribute('aria-labelledby', groupLabelId);
    optionsList.tabIndex = -1;

    const options = this.core.config.options[component.key] || [];

    options.forEach((option) => {
      const listItem = document.createElement('div');
      listItem.className = 'specifikit-dropdown-option specifikit-component-dropdown__option';
      listItem.id = `specifikit-option-${component.key}-${option.handle}`;
      listItem.dataset.value = option.handle;
      listItem.setAttribute('role', 'option');
      listItem.setAttribute('aria-selected', 'false');

      const swatchElement = this.createSwatchElement(option);
      if (swatchElement) {
        listItem.appendChild(swatchElement);
      }

      const labelSpan = document.createElement('span');
      labelSpan.className = 'specifikit-component-dropdown__option-text';
      labelSpan.textContent = option.label;
      listItem.appendChild(labelSpan);

      if (option.price_modifier > 0) {
        const priceSpan = document.createElement('span');
        priceSpan.className = 'specifikit-price-mod';
        priceSpan.textContent = `(+${this.core.formatMoney(option.price_modifier * 100)})`;
        listItem.appendChild(priceSpan);
      }
      optionsList.appendChild(listItem);
    });

    dropdownWrapper.appendChild(selectedDisplay);
    dropdownWrapper.appendChild(optionsList);
    optionsContainer.parentNode.insertBefore(dropdownWrapper, optionsContainer);

    const defaultCheckedRadio = optionsContainer.querySelector('input:checked');
    if (defaultCheckedRadio) {
      this.updateCustomDropdownDisplay(dropdownWrapper, defaultCheckedRadio.value);
    }

    const optionGroup = optionsContainer.closest('.specifikit-option-group');
    const accordionItem = optionsContainer.closest('.specifikit-accordion-item');

    const closeDropdown = () => {
      dropdownWrapper.classList.remove('is-open');
      selectedDisplay.setAttribute('aria-expanded', 'false');
      if (optionGroup) optionGroup.classList.remove('dropdown-is-active');
      if (accordionItem) accordionItem.classList.remove('child-dropdown-open');
    };

    const openDropdown = () => {
      document.querySelectorAll('.specifikit-component-dropdown.is-open').forEach(openDropdown => {
        if (openDropdown !== dropdownWrapper) {
          openDropdown.classList.remove('is-open');
          openDropdown.querySelector('.specifikit-component-dropdown__trigger')?.setAttribute('aria-expanded', 'false');
          openDropdown.closest('.specifikit-option-group')?.classList.remove('dropdown-is-active');
          openDropdown.closest('.specifikit-accordion-item')?.classList.remove('child-dropdown-open');
        }
      });
      
      dropdownWrapper.classList.add('is-open');
      selectedDisplay.setAttribute('aria-expanded', 'true');
      if (optionGroup) optionGroup.classList.add('dropdown-is-active');
      if (accordionItem) accordionItem.classList.add('child-dropdown-open');
      
      const selected = optionsList.querySelector('[aria-selected="true"]');
      this._focusDropdownOption(selected || optionsList.querySelector('.specifikit-component-dropdown__option'));
    };
    
    selectedDisplay.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdownWrapper.classList.contains('is-open') ? closeDropdown() : openDropdown();
    });

    optionsList.addEventListener('click', (e) => {
      const targetOption = e.target.closest('.specifikit-component-dropdown__option');
      if (!targetOption) return;
      this._selectDropdownOption(targetOption, optionsContainer, dropdownWrapper);
      closeDropdown();
      selectedDisplay.focus();
    });

    dropdownWrapper.addEventListener('keydown', (e) => {
      this._handleDropdownKeyDown(e, dropdownWrapper, optionsContainer);
    });
  }

  _focusDropdownOption(optionEl) {
    if (!optionEl) return;
    const list = optionEl.parentElement;
    list.querySelectorAll('.specifikit-component-dropdown__option').forEach(opt => opt.classList.remove('is-focused'));
    optionEl.classList.add('is-focused');
    optionEl.scrollIntoView({ block: 'nearest' });
  }

  _selectDropdownOption(optionEl, optionsContainer, dropdownWrapper) {
    if (!optionEl) return;
    const value = optionEl.dataset.value;
    const targetRadio = optionsContainer.querySelector(`input[value="${value}"]`);

    if (targetRadio && !targetRadio.checked) {
        targetRadio.checked = true;
        targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateCustomDropdownDisplay(dropdownWrapper, value);
    }
  }

  _handleDropdownKeyDown(e, dropdownWrapper, optionsContainer) {
    const isOpen = dropdownWrapper.classList.contains('is-open');
    const optionsList = dropdownWrapper.querySelector('.specifikit-component-dropdown__list');
    const options = Array.from(optionsList.querySelectorAll('.specifikit-component-dropdown__option'));
    const focusedOption = optionsList.querySelector('.is-focused');
    const selectedDisplay = dropdownWrapper.querySelector('.specifikit-component-dropdown__trigger');
    
    if (e.key === 'Escape') {
        if (isOpen) {
            e.preventDefault();
            selectedDisplay.click(); // Triggers close logic
        }
        return;
    }

    if (document.activeElement === selectedDisplay && !isOpen) {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
            e.preventDefault();
            selectedDisplay.click(); // Triggers open logic
        }
        return;
    }

    if (!isOpen) return;
    
    let currentIndex = focusedOption ? options.indexOf(focusedOption) : -1;
    
    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault();
            currentIndex = (currentIndex + 1) % options.length;
            this._focusDropdownOption(options[currentIndex]);
            break;
            
        case 'ArrowUp':
            e.preventDefault();
            currentIndex = (currentIndex - 1 + options.length) % options.length;
            this._focusDropdownOption(options[currentIndex]);
            break;
            
        case 'Home':
            e.preventDefault();
            this._focusDropdownOption(options[0]);
            break;
            
        case 'End':
            e.preventDefault();
            this._focusDropdownOption(options[options.length - 1]);
            break;
            
        case 'Tab':
            selectedDisplay.click();
            break;

        case 'Enter':
        case ' ':
            e.preventDefault();
            if (focusedOption) {
                this._selectDropdownOption(focusedOption, optionsContainer, dropdownWrapper);
                selectedDisplay.click(); // Triggers close logic
            }
            break;
    }
  }

  setupSwatches() {
    const swatchConfig = this.core.config.swatchConfig;
    if (!swatchConfig || !swatchConfig.enable) return;

    this.core.config.components?.forEach(comp => {
      if (!comp.enabled) return;

      const componentOptions = this.core.config.options[comp.key] || [];
      const optionElements = this.container.querySelectorAll(`[data-specifikit-option-input="${comp.key}"]`);

      optionElements.forEach(radioInput => {
        const optionConfig = componentOptions.find(opt => opt.handle === radioInput.value);
        if (!optionConfig) return;

        const swatchElement = this.createSwatchElement(optionConfig);

        if (swatchElement) {
          const optionButton = radioInput.closest('label').querySelector('.specifikit-option-button');
          if (optionButton && !optionButton.querySelector('.specifikit-swatch')) {
            optionButton.prepend(swatchElement);
          }
        }
      });
    });
  }

  createSwatchElement(optionConfig) {
    const swatch = document.createElement('span');
    swatch.className = 'specifikit-swatch';

    if (optionConfig.image_url) {
      swatch.style.backgroundImage = `url(${optionConfig.image_url})`;
      swatch.classList.add('specifikit-swatch-image');
      return swatch;
    }

    const colors = this.core.config.swatchConfig.colors || {};
    let colorValue = null;

    if (colors[optionConfig.handle]) {
      colorValue = colors[optionConfig.handle];
    } else if (optionConfig.label && colors[optionConfig.label.toLowerCase()]) {
      colorValue = colors[optionConfig.label.toLowerCase()];
    } else {
      for (const key in colors) {
        if (optionConfig.handle.includes(key) || optionConfig.label.toLowerCase().includes(key)) {
          colorValue = colors[key];
          break;
        }
      }
    }

    if (colorValue) {
      if (typeof colorValue === 'string' && colorValue.includes(';')) {
        swatch.style.background = `linear-gradient(45deg, ${colorValue.replace(/;/g, ', ')})`;
      } else {
        swatch.style.backgroundColor = colorValue;
      }
      return swatch;
    }

    return null;
  }

  setupAccordionLayout() {
    if (this.core.config.optionsLayout !== 'accordion') return;
    this.logger.info('✨ Applying accordion layout');

    const optionsContainer = this.container.querySelector('[data-specifikit-options-container]');
    if (!optionsContainer) return;

    const groups = optionsContainer.querySelectorAll('.specifikit-option-group');
    if (groups.length < 2) return;

    const styleId = 'specifikit-accordion-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .specifikit-accordion-header { cursor: pointer; width: 100%; background: none; border: none; padding: 0.75rem 0; margin: 0; text-align: left; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--specifikit-border-color); }
        .specifikit-accordion-header:hover .specifikit-option-label { color: var(--specifikit-primary-color); }
        .specifikit-accordion-header .specifikit-option-label { margin-bottom: 0; transition: color 0.2s ease; }
        .specifikit-accordion-icon { transition: transform 0.3s ease; width: 1.2em; height: 1.2em; color: var(--specifikit-primary-color); }
        .specifikit-accordion-item.specifikit-active .specifikit-accordion-icon { transform: rotate(180deg); }
        .specifikit-accordion-content { max-height: 0; overflow: hidden; transition: max-height 0.4s ease-out; }
        .specifikit-accordion-item.specifikit-active .specifikit-accordion-content { max-height: 1500px; transition: max-height 0.4s ease-in; }
        .specifikit-accordion-content-inner { padding: 1rem 0; }
      `;
      document.head.appendChild(style);
    }

    groups.forEach((group, index) => {
      const label = group.querySelector('.specifikit-option-label');
      let contentElements = Array.from(group.children).filter(child => !child.matches('.specifikit-option-label'));

      if (!label || contentElements.length === 0) return;

      group.classList.add('specifikit-accordion-item');
      const header = document.createElement('button');
      header.className = 'specifikit-accordion-header';
      header.type = 'button';
      header.setAttribute('aria-expanded', 'false');

      const icon = document.createElement('span');
      icon.className = 'specifikit-accordion-icon';
      icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>`;

      header.appendChild(label);
      header.appendChild(icon);

      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'specifikit-accordion-content';
      const contentInner = document.createElement('div');
      contentInner.className = 'specifikit-accordion-content-inner';

      contentElements.forEach(el => contentInner.appendChild(el));
      contentWrapper.appendChild(contentInner);
      group.innerHTML = '';
      group.appendChild(header);
      group.appendChild(contentWrapper);

      const contentId = `specifikit-accordion-content-${index}`;
      contentWrapper.id = contentId;
      header.setAttribute('aria-controls', contentId);

      header.addEventListener('click', () => {
        const isActive = group.classList.toggle('specifikit-active');
        header.setAttribute('aria-expanded', String(isActive));
      });
      if (index === 0) {
        group.classList.add('specifikit-active');
        header.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /**
   * ========================================================================
   * START: ACTION BUTTON GATEKEEPER
   * This observer monitors the action button area. It intelligently
   * identifies third-party additions, styling true buttons for consistency
   * and relocating non-button elements (like stock notices) to a
   * dedicated notification area, ensuring a clean and reliable UI.
   * ========================================================================
   */
  setupActionButtonObserver() {
    // Target the new, specific wrapper for focused observation.
    const targetNode = this.elements.actionButtonsWrapper;

    // Ensure both the source and destination containers exist.
    if (!targetNode || !this.elements.notificationsContainer) {
      this.logger.warn('Gatekeeper: Action button wrapper or notifications container not found. Cannot ensure layout integrity.');
      return;
    }

    const styleActionButton = (button) => {
      // Failsafe: Don't restyle our own buttons or buttons we've already processed.
      if (button.dataset.specifikitStyled === 'true' || button.matches('[data-specifikit-submit-button], .specifikit-btn')) {
        return;
      }

      this.logger.log('Gatekeeper: Intercepted third-party action button. Applying styles.', button);

      // Clean inline styles from icons to allow CSS currentColor to work
      const icons = button.querySelectorAll('svg, i, [class*="icon"]');
      icons.forEach(icon => {
        const elementsToClean = [icon, ...icon.querySelectorAll('*')];
        elementsToClean.forEach(el => {
          if (el.style.cssText) {
            const originalStyles = el.style.cssText.split(';').map(s => s.trim()).filter(Boolean);
            const safeStyles = originalStyles.filter(style => {
              const property = style.split(':')[0].trim().toLowerCase();
              return !['color', 'fill', 'stroke', 'background', 'background-color'].includes(property);
            });
            if (safeStyles.length < originalStyles.length) {
              el.style.cssText = safeStyles.join('; ');
            }
          }
          if (el.hasAttribute('fill') && el.getAttribute('fill').toLowerCase() !== 'none') el.removeAttribute('fill');
          if (el.hasAttribute('stroke') && el.getAttribute('stroke').toLowerCase() !== 'none') el.removeAttribute('stroke');
          if (el.hasAttribute('color')) el.removeAttribute('color');
        });
      });

      // Remove conflicting classes from known apps to allow our styles to take precedence.
      const conflictingClasses = ['nm-wishlist-button', 'nm-PRODUCT_PAGE-wishlist-button', 'restock-alerts-notify-button', 'PRODUCT_PAGE-notify-button', 'notifyButtonStyle', 'button--primary', 'Button--primary', 'button-primary', 'Button-primary'];
      conflictingClasses.forEach(cls => button.classList.remove(cls));

      // More robust style reset, preserving critical layout styles like 'display: flex !important'
      if (button.hasAttribute('style')) {
        const importantDisplay = button.style.getPropertyValue('display');
        const isFlex = importantDisplay.includes('flex');
        button.removeAttribute('style');
        if (isFlex) {
          button.style.display = 'flex';
          button.style.justifyContent = 'center';
          button.style.alignItems = 'center';
        }
      }

      // Clean up data attributes from known apps
      Object.keys(button.dataset).forEach(key => {
        if (key.startsWith('nm') || key.includes('wishlist') || key.includes('style')) {
          delete button.dataset[key];
        }
      });

      // Apply our consistent secondary button style.
      button.classList.add('specifikit-btn', 'specifikit-btn-md', 'specifikit-btn-secondary');

      // Capture the wishlist button for modal logic if it hasn't been captured yet.
      const isWishlistButton = button.matches('[class*="wishlist"]') || button.textContent.toLowerCase().includes('wishlist');
      if (isWishlistButton && !this.themeWishlistButton.isCaptured) {
        this.logger.log('Gatekeeper: Capturing theme wishlist button for modal management.', button);
        this.themeWishlistButton.element = button;
        this.themeWishlistButton.originalParent = button.parentElement;
        this.themeWishlistButton.originalNextSibling = button.nextSibling;
        this.themeWishlistButton.isCaptured = true;
      }

      button.dataset.specifikitStyled = 'true';
    };

    const isButtonLike = (node) => {
      // A check for what constitutes an "action button".
      if (!node || node.nodeType !== 1) return false;

      // Direct match on common button elements or roles.
      if (node.matches('button, a[href], [role="button"]')) {
        return true;
      }

      // Check for common class name patterns.
      if (node.classList && Array.from(node.classList).some(cls => cls.toLowerCase().includes('button'))) {
        return true;
      }

      // Some apps wrap their button in a single div, check if the node has a button as a direct child.
      if (node.children.length > 0 && isButtonLike(node.querySelector('button, a[href]'))) {
        return true;
      }

      return false;
    };

    const processNode = (node) => {
      if (node.nodeType !== 1 || node.dataset.specifikitProcessed === 'true') {
        return;
      }

      if (isButtonLike(node)) {
        this.logger.log('Gatekeeper: Identified action button or wrapper. Styling and retaining.', node);
        // If the node itself is the button, style it. If it's a wrapper, find and style the button inside.
        const buttonElement = node.matches('button, a[href]') ? node : node.querySelector('button, a[href]');
        if (buttonElement) {
            styleActionButton(buttonElement);
        }
      } else {
        // It's a non-button element (like the low-stock div), so we relocate it.
        this.logger.log('Gatekeeper: Relocating non-button element to notifications container.', node);
        this.elements.notificationsContainer.appendChild(node);
      }

      // Mark the top-level node as processed to avoid re-evaluating it.
      node.dataset.specifikitProcessed = 'true';
    };

    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(processNode);
        }
      }
    };

    // Disconnect old observer if it exists
    if (this.core.actionButtonObserver) this.core.actionButtonObserver.disconnect();

    this.core.actionButtonObserver = new MutationObserver(observerCallback);
    this.core.actionButtonObserver.observe(targetNode, {
      childList: true,
      subtree: true // Observe subtree to catch apps that inject deeply nested elements.
    });

    // Initial scan on load for elements that rendered before the observer attached.
    this.logger.log('Gatekeeper: Performing initial scan of action button wrapper.');
    Array.from(targetNode.children).forEach(processNode);

    this.logger.info('✅ Action Button Gatekeeper is active.');
  }
  /**
   * ========================================================================
   * END: ACTION BUTTON GATEKEEPER
   * ========================================================================
   */

  syncQuantity() {
    const transplantedQuantityInput = this.container.querySelector('[data-specifikit-placeholder="form-container"] input[name="quantity"], [data-specifikit-placeholder="form-container"] quantity-input input');
    if (transplantedQuantityInput && this.elements.quantityInput) {
      this.elements.quantityInput.value = transplantedQuantityInput.value;
    }
  }

  updateAvailabilityUI() {
    const liveProductData = this.core.liveProductData;
    if (!liveProductData || !liveProductData.variants) return;
    const sizeComponent = this.core.config.components?.find(c => c.key === 'size' && c.enabled);
    if (!sizeComponent || !this.elements.componentOptions.size) return;

    this.elements.componentOptions.size.forEach(input => {
      const variantId = parseInt(input.value, 10);
      const liveVariant = liveProductData.variants.find(v => v.id === variantId);

      if (liveVariant) {
        const isAvailable = liveVariant.available;
        const label = input.closest('label');
        const soldOutBadge = label ? label.querySelector('.specifikit-sold-out-badge') : null;

        input.disabled = !isAvailable;

        if (label) {
          label.classList.toggle('specifikit-sold-out', !isAvailable);
        }
        if (soldOutBadge) {
          soldOutBadge.style.display = isAvailable ? 'none' : 'inline-block';
        }

        if (input.checked && !isAvailable) {
          this.showNotification(`Size "${liveVariant.title}" just sold out. Please choose another.`, 'error');
          if (this.elements.submitButton) this.elements.submitButton.disabled = true;
        }
      }
    });

    const sizeComponent2 = this.core.config.components?.find(c => c.key === 'size' && c.enabled);
    if (sizeComponent2) {
      const currentVariantInput = this.container.querySelector('[data-specifikit-option-input="size"]:checked');
      if (this.elements.submitButton && currentVariantInput && !currentVariantInput.disabled) {
        this.elements.submitButton.disabled = false;
      }
    }
  }

  updateAddonAvailabilityUI() {
    this.elements.addonInputs.forEach(input => {
      if (input.dataset.addonType !== 'checkbox') return;

      const productHandle = input.dataset.addonProductHandle;
      const variantId = this.core.gidToId(input.dataset.addonVariantId);
      if (!productHandle || !variantId) return;

      const liveAddonProduct = this.core.liveAddonData.get(productHandle);
      if (!liveAddonProduct || !liveAddonProduct.variants) {
        this.logger.warn(`No live data found for addon handle: ${productHandle}`);
        return;
      }

      const liveVariant = liveAddonProduct.variants.find(v => v.id === parseInt(variantId, 10));

      if (liveVariant) {
        const isAvailable = liveVariant.available;
        const label = input.closest('label');

        input.disabled = !isAvailable;

        if (label) {
          const textSpan = label.querySelector('span');
          if (textSpan) {
            textSpan.innerHTML = textSpan.innerHTML.replace(/\s*<small.*?>\(Out of stock\)<\/small>/i, '');
            if (!isAvailable) {
              textSpan.innerHTML += ' <small style="color: #ef4444;">(Out of stock)</small>';
            }
          }
        }
        if (input.checked && !isAvailable) {
          input.checked = false;
          input.dispatchEvent(new Event('change', {
            bubbles: true
          }));
          const addonConfig = this.core.config.addons.find(a => a.id === input.dataset.addonId);
          if (addonConfig) {
            this.showNotification(`Add-on "${addonConfig.label}" just sold out.`, 'info');
          }
        }
      }
    });
  }

  applyConditionalRules() {
    const {
      state,
      conditionalRulesEngine,
      capitalize
    } = this.core;
    if (!conditionalRulesEngine) return;

    if (state.isInitialState) return;

    const currentState = {
      size: state.size,
      componentSelections: state.componentSelections
    };
    const {
      disable,
      enable,
      showAddon,
      hideAddon
    } = conditionalRulesEngine.evaluate(currentState);

    enable.forEach(({
      component,
      option
    }) => {
      const input = this.container.querySelector(`[data-specifikit-option-input="${component}"][value="${option}"]`);
      if (input && input.disabled) {
        const label = input.closest('label');
        if (label && !label.classList.contains('specifikit-sold-out')) {
          input.disabled = false;
          label.classList.remove('specifikit-disabled');
          label.removeAttribute('title');
        }
      }
    });

    disable.forEach(({
      component,
      option,
      reason
    }) => {
      const input = this.container.querySelector(`[data-specifikit-option-input="${component}"][value="${option}"]`);
      if (input && !input.disabled) {
        input.disabled = true;
        const label = input.closest('label');
        if (label) {
          label.classList.add('specifikit-disabled');
          label.setAttribute('title', `Not available with ${reason}`);
        }

        if (input.checked) {
          if (input.type === 'checkbox') {
            input.checked = false;
            input.dispatchEvent(new Event('change', {
              bubbles: true
            }));
          } else {
            const firstAvailable = this.container.querySelector(`[data-specifikit-option-input="${component}"]:not(:disabled)`);
            if (firstAvailable) {
              firstAvailable.checked = true;
              firstAvailable.dispatchEvent(new Event('change', {
                bubbles: true
              }));
            }
          }
        }
      }
    });

    const uniqueHide = [...new Set(hideAddon)];
    uniqueHide.forEach(addonId => {
      const input = this.container.querySelector(`[data-addon-id="${addonId}"]`);
      if (!input) return;
      const itemContainer = input.closest('.specifikit-addon-item');
      if (itemContainer && itemContainer.style.display !== 'none') {
        itemContainer.style.display = 'none';
        if (input.type === 'checkbox' && input.checked) {
          input.checked = false;
          input.dispatchEvent(new Event('change', {
            bubbles: true
          }));
        } else if (input.type === 'text' && input.value) {
          input.value = '';
          input.dispatchEvent(new Event('input', {
            bubbles: true
          }));
        }
      }
    });

    const uniqueShow = [...new Set(showAddon)];
    uniqueShow.forEach(addonId => {
      const input = this.container.querySelector(`[data-addon-id="${addonId}"]`);
      if (!input) return;
      const itemContainer = input.closest('.specifikit-addon-item');
      if (itemContainer && itemContainer.style.display === 'none') {
        itemContainer.style.display = '';
      }
    });
  }

  updateHiddenPropertyInputs() {
    const {
      state,
      capitalize
    } = this.core;
    if (state.isInitialState) return;

    this.core.config.components?.forEach(comp => {
      if (!comp.enabled) return;

      const propInput = this.container.querySelector(`[data-specifikit-prop-input="${comp.key}"]`);
      if (!propInput) return;

      let value = '';
      if (comp.key === 'size') {
        const sizeName = this.container.querySelector(`[data-size="${state.size}"]`)?.dataset.sizeName || capitalize(state.size);
        value = sizeName || '';
      } else {
        const selection = state.componentSelections[comp.key];
        if (Array.isArray(selection)) {
          if (selection.length > 0) {
            value = selection.map(s => s.name).join(', ');
          }
        } else if (selection) {
          value = selection.name || '';
        }
      }
      propInput.value = value;
    });
  }

  async updateImage() {
    const {
      state,
      mode
    } = this.core;
    if (state.isInitialState) {
      this.logger.log('🖼️ In initial state, skipping image update.');
      return;
    }

    if (mode === 'customizer' && !state.view && this.core.config.imageConfig?.views?.length > 0) {
      const defaultView = this.core.config.imageConfig.views.find(v => v.default === true) || this.core.config.imageConfig.views[0];
      this.core.handleViewChange(defaultView.handle, false);
    }

    const imageConfig = this.core.config.imageConfig;
    if (!imageConfig) {
      this.logger.log('ℹ️ No image config found');
      return;
    }

    const imageMode = imageConfig.mode || 'simple';

    if (imageMode === 'simple') {
      await this.updateSimpleImage(imageConfig);
    } else if (imageMode === 'advanced') {
      await this.updateLayeredImage(imageConfig);
    } else if (imageMode === 'canvas') {
      await this.updateCanvasImage(imageConfig);
    }
  }

  async updateSimpleImage(imageConfig) {
    const { state } = this.core;
    const buildKey = (template) => {
      if (!template) return null;
      let key = template;

      const viewsArray = this.core.config.imageConfig?.views || [];
      const currentView = state.view || (viewsArray.find(v => v.default === true)?.handle || viewsArray[0]?.handle || '');

      key = key.replace('{view}', currentView);

      const sizeComponent = this.core.config.components?.find(c => c.key === 'size' && c.enabled);
      if (sizeComponent) {
        key = key.replace('{size}', state.size || '');
      }

      for (const [compKey, selection] of Object.entries(state.componentSelections)) {
        const componentConfig = this.core.config.components.find(c => c.key === compKey);
        if (componentConfig?.display_style === 'checkbox') continue;

        if (selection && selection.handle) {
          key = key.replace(`{${compKey}}`, selection.handle);
        }
      }
      return key;
    };

    const { images, key_template, fallback_chain, fallback } = imageConfig;
    let imageUrl = null;
    const primaryKey = buildKey(key_template);

    if (images && images[primaryKey]) {
      imageUrl = images[primaryKey];
    }

    if (!imageUrl && Array.isArray(fallback_chain)) {
      for (const template of fallback_chain) {
        const fallbackKey = buildKey(template);
        if (fallbackKey && images && images[fallbackKey]) {
          imageUrl = images[fallbackKey];
          break;
        }
      }
    }

    if (!imageUrl) {
      imageUrl = fallback || this.elements.mainImage?.src;
    }

    if (!imageUrl) {
      this.showImageError(true, 'Preview unavailable');
      return;
    }

    this.showSpinner(true);
    this.showImageError(false);

    try {
      await this.core.loadImage(imageUrl);

      document.querySelectorAll('[data-specifikit-image-simple]').forEach(img => {
        const parent = img.parentElement;
        if (parent && parent.dataset.specifikitImageLayerStack !== undefined) {
          const newImg = document.createElement('img');
          newImg.dataset.specifikitImageSimple = '';
          Object.assign(newImg, {
            src: imageUrl,
            alt: img.alt,
            loading: 'eager'
          });
          newImg.onload = () => this.updateImageContainerAspectRatio(newImg);
          parent.innerHTML = '';
          parent.appendChild(newImg);
          if (newImg.complete) this.updateImageContainerAspectRatio(newImg);
        } else {
          img.src = imageUrl;
          img.onload = () => this.updateImageContainerAspectRatio(img);
          if (img.complete) this.updateImageContainerAspectRatio(img);
        }
      });

    } catch (error) {
      this.logger.error('Image failed:', error);
      this.showImageError(true, 'Preview unavailable');
    } finally {
      this.showSpinner(false);
    }
  }

  async updateLayeredImage(imageConfig) {
    const { state } = this.core;
    const buildKey = (template) => {
      let key = template.replace('{size}', state.size || '');

      const viewsArray = this.core.config.imageConfig?.views || [];
      const currentView = state.view || (viewsArray.find(v => v.default === true)?.handle || viewsArray[0]?.handle || '');
      
      key = key.replace('{view}', currentView);
      
      for (const [compKey, selection] of Object.entries(state.componentSelections)) {
        const componentConfig = this.core.config.components.find(c => c.key === compKey);
        if (componentConfig?.display_style === 'checkbox') continue;

        if (selection && selection.handle) {
          key = key.replace(`{${compKey}}`, selection.handle);
        }
      }
      return key;
    };

    const key = buildKey(imageConfig.key_template);
    let layerConfig = imageConfig.layers?.[key] || imageConfig.fallback_layers || [];

    if (!Array.isArray(layerConfig) || layerConfig.length === 0) {
      this.logger.warn('No layers for key:', key);
      this.showImageError(true, 'Preview unavailable');
      return;
    }

    this.showSpinner(true);
    this.showImageError(false);

    try {
      const sortedLayers = [...layerConfig].sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
      const imagePromises = sortedLayers.map(layer => this.core.loadImage(layer.url));
      await Promise.all(imagePromises);

      document.querySelectorAll('[data-specifikit-image-layer-stack]').forEach(stack => {
        const imageContainer = stack.closest('.specifikit-main-product-image');
        if (imageContainer) {
          imageContainer.style.aspectRatio = '';
        }

        const oldLayers = Array.from(stack.children);
        oldLayers.forEach(layer => {
          layer.style.transition = 'opacity 0.4s ease-out';
          layer.style.opacity = 0;
        });

        const newLayers = sortedLayers.map(layer => {
          const img = document.createElement('img');
          img.src = layer.url;
          img.alt = layer.name || 'Layer';
          img.style.cssText = `
              position: absolute; top: 0; left: 0; width: 100%; height: 100%;
              object-fit: contain; z-index: ${layer.z_index || 0};
              opacity: 0; transition: opacity 0.4s ease-in;
            `;
          stack.appendChild(img);
          return img;
        });

        requestAnimationFrame(() => {
          newLayers.forEach(img => {
            img.style.opacity = 1;
          });
        });

        setTimeout(() => {
          oldLayers.forEach(layer => layer.remove());
        }, 400);
      });

      this.logger.log(`✅ Loaded and displaying ${sortedLayers.length} layers`);

    } catch (error) {
      this.logger.error('Layer load failed:', error);
      this.showImageError(true, 'Preview unavailable');
      document.querySelectorAll('[data-specifikit-image-layer-stack]').forEach(stack => stack.innerHTML = '');
    } finally {
      setTimeout(() => this.showSpinner(false), 50);
    }
  }

  async updateCanvasImage(imageConfig) {
    const canvasConfig = imageConfig.canvas;
    if (!canvasConfig || !canvasConfig.base_image || !canvasConfig.layers) {
      this.logger.error('Canvas Mode: Configuration is incomplete. Missing base_image or layers definition.');
      this.showImageError(true, 'Builder config error');
      return;
    }

    this.showSpinner(true);
    this.showImageError(false);

    const activeLayers = [];
    for (const [compKey, selection] of Object.entries(this.core.state.componentSelections)) {
      if (Array.isArray(selection)) {
        selection.forEach(subSelection => {
          const layerKey = `${compKey}:${subSelection.handle}`;
          if (canvasConfig.layers[layerKey]) {
            activeLayers.push({ ...canvasConfig.layers[layerKey],
              key: layerKey
            });
          }
        });
      } else if (selection?.handle) {
        const layerKey = `${compKey}:${selection.handle}`;
        if (canvasConfig.layers[layerKey]) {
          activeLayers.push({ ...canvasConfig.layers[layerKey],
            key: layerKey
          });
        }
      }
    }

    const sortedLayers = activeLayers.sort((a, b) => (a.z_index || 0) - (b.z_index || 0));

    try {
      const imagePromises = sortedLayers.map(layer => this.core.loadImage(layer.url));
      await Promise.all(imagePromises);

      document.querySelectorAll('[data-specifikit-image-layer-stack]').forEach(stack => {
        const oldLayers = Array.from(stack.querySelectorAll('img:not([data-specifikit-canvas-base])'));
        oldLayers.forEach(layer => {
          layer.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
          layer.style.opacity = 0;
          layer.style.transform = 'scale(0.95)';
        });

        const newLayers = sortedLayers.map(layer => {
          const img = document.createElement('img');
          img.src = layer.url;
          img.alt = layer.name || `Layer for ${layer.key}`;
          img.dataset.layerKey = layer.key;
          img.style.cssText = `
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            object-fit: contain; z-index: ${layer.z_index || 0};
            opacity: 0; transform: scale(0.95);
            transition: opacity 0.4s ease-in, transform 0.4s ease-in;
          `;
          stack.appendChild(img);
          return img;
        });

        requestAnimationFrame(() => {
          newLayers.forEach(img => {
            img.style.opacity = 1;
            img.style.transform = 'scale(1)';
          });
        });

        setTimeout(() => {
          oldLayers.forEach(layer => layer.remove());
        }, 400);
      });

      this.logger.log(`✅ Canvas Mode: Displaying ${sortedLayers.length} item layers.`);

    } catch (error) {
      this.logger.error('Canvas layer load failed:', error);
      this.showImageError(true, 'Could not load item');
    } finally {
      this.showSpinner(false);
    }
  }

  /**
   * Engineered updatePrice function to handle sale prices and badges.
   * This function is designed to be universal, updating all price displays
   * (main page and modal) simultaneously to ensure perfect synchronization.
   */
  updatePrice() {
    const totalPrice = this.core.getTotalPrice();
    const totalCompareAtPrice = this.core.getTotalCompareAtPrice();
    const isOnSale = totalCompareAtPrice > 0 && totalCompareAtPrice > totalPrice;

    this.logger.groupCollapsed(`💰 Price Update (On Sale: ${isOnSale})`);
    this.logger.log('Total Price:', totalPrice);
    this.logger.log('Total Compare At Price:', totalCompareAtPrice);
    this.logger.groupEnd();

    const formattedPrice = this.core.formatMoney(totalPrice * 100);
    const formattedCompareAtPrice = this.core.formatMoney(totalCompareAtPrice * 100);

    // UNIVERSAL SELECTORS: These will find the price elements whether they are on the page or in an open modal.
    const priceWrappers = document.querySelectorAll('[data-specifikit-price-wrapper]');
    const regularPriceElements = document.querySelectorAll('[data-specifikit-price-regular]');
    const salePriceElements = document.querySelectorAll('[data-specifikit-price-sale]');
    const comparePriceElements = document.querySelectorAll('[data-specifikit-price-compare]');
    const saleBadgeElements = document.querySelectorAll('.specifikit-sale-badge');
    const submitPriceElements = document.querySelectorAll('[data-specifikit-submit-price]');
    const paymentTermsElements = document.querySelectorAll('shopify-payment-terms');

    // 1. Toggle the 'on-sale' class on all price wrappers.
    priceWrappers.forEach(wrapper => {
      wrapper.classList.toggle('on-sale', isOnSale);
    });

    // 2. Update the text content of all price elements.
    regularPriceElements.forEach(el => {
      el.textContent = formattedPrice;
    });

    salePriceElements.forEach(el => {
      el.textContent = formattedPrice;
    });

    comparePriceElements.forEach(el => {
      el.textContent = formattedCompareAtPrice;
    });

    // 3. Update the visibility of the sale badge.
    saleBadgeElements.forEach(el => {
      // The theme setting in Liquid controls if the badge *can* be shown.
      // JS handles the dynamic visibility based on price changes.
      const initiallyEnabled = !el.hasAttribute('style') || !el.style.display || el.style.display !== 'none';
      if (initiallyEnabled) {
          el.style.display = isOnSale ? 'inline-flex' : 'none';
      }
    });

    // 4. Update the price on the submit button.
    submitPriceElements.forEach(el => {
      el.textContent = formattedPrice;
    });

    // 5. Update any payment terms app block.
    paymentTermsElements.forEach(el => {
      // Ensure the element is within a Specifikit context to avoid conflicts
      if (el.closest('[data-specifikit-container], .specifikit-modal-view')) {
        el.setAttribute('amount', (totalPrice * 100).toString());
      }
    });
  }

  updateSummary() {
    const {
      state,
      capitalize
    } = this.core;
    if (state.isInitialState) {
      document.querySelectorAll('[data-specifikit-summary-item]').forEach(el => {
        if (el.id.includes('addons')) {
          el.style.display = 'none';
        } else if (el.querySelector('span')) {
          el.querySelector('span').textContent = '-';
        } else {
          el.textContent = '-';
        }
      });
      return;
    }

    const activeScope = this.modal.isOpen && this.modal.instance ? this.modal.instance : this.container;

    if (!activeScope) {
      this.logger.error("Could not determine active scope for summary update.");
      return;
    }

    this.core.config.components?.forEach(comp => {
      if (!comp.enabled) return;

      const summaryEl = activeScope.querySelector(`[data-specifikit-summary-item="${comp.key}"]`);
      if (!summaryEl) {
        return;
      }

      let summaryText = '-';
      if (comp.key === 'size') {
        const sizeInput = this.container.querySelector(`[data-size="${state.size}"]`);
        summaryText = sizeInput?.dataset.sizeName || capitalize(state.size) || '-';
      } else {
        const selection = state.componentSelections[comp.key];
        if (Array.isArray(selection)) {
          if (selection.length > 0) {
            summaryText = selection.map(s => s.name).join(', ');
          }
        } else if (selection) {
          summaryText = selection.name || '-';
        }
      }
      summaryEl.textContent = summaryText;
    });

    const addonsContainer = activeScope.querySelector('[data-specifikit-summary-item="addons"]');
    const addonsList = [];

    for (const [addonId, addon] of Object.entries(state.addons)) {
      if (addon.type === 'checkbox' && addon.enabled) {
        addonsList.push(addon.label);
      } else if (addon.type === 'text' && addon.value) {
        addonsList.push(`${addon.label}: "${addon.value}"`);
      }
    }

    if (addonsContainer) {
      if (addonsList.length > 0) {
        const addonsText = activeScope.querySelector('#specifikit-summary-addons');
        if (addonsText) addonsText.textContent = addonsList.join(', ');
        addonsContainer.style.display = 'list-item';
      } else {
        addonsContainer.style.display = 'none';
      }
    }
  }

  /**
   * Method to handle UI feedback for missing selections,
   * keeping DOM manipulation out of the Core class.
   */
  signalMissingSelection() {
    const firstOptionGroup = this.container.querySelector('.specifikit-option-group');
    if (firstOptionGroup) {
      firstOptionGroup.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      firstOptionGroup.animate([{
        transform: 'translateX(0)'
      }, {
        transform: 'translateX(-5px)'
      }, {
        transform: 'translateX(5px)'
      }, {
        transform: 'translateX(0)'
      }], {
        duration: 400,
        easing: 'ease-in-out'
      });
    }
  }

  showSpinner(show) {
    document.querySelectorAll('[data-specifikit-image-spinner]').forEach(el => {
      el.style.display = show ? 'flex' : 'none';
    });
    const opacity = show ? '0.5' : '1';
    document.querySelectorAll('[data-specifikit-image-simple], [data-specifikit-image-layer-stack]').forEach(el => {
      el.style.opacity = opacity;
    });
  }

  showImageError(show, message = '') {
    document.querySelectorAll('[data-specifikit-image-error]').forEach(el => {
      el.style.display = show ? 'flex' : 'none';
      if (message) {
        const p = el.querySelector('p');
        if (p) p.textContent = message;
      }
    });
  }

  setSubmitButtonState(state) {
    if (!this.elements.submitButton) return;

    const textSpan = this.elements.submitButton.querySelector('.specifikit-btn-text');
    const spinner = this.elements.submitButton.querySelector('.specifikit-btn-spinner');

    this.elements.submitButton.disabled = (state === 'loading');

    if (state === 'loading') {
      if (textSpan) textSpan.style.display = 'none';
      if (spinner) spinner.style.display = 'inline-flex';
      this.elements.submitButton.classList.add('loading');
    } else {
      if (textSpan) textSpan.style.display = 'inline';
      if (spinner) spinner.style.display = 'none';
      this.elements.submitButton.classList.remove('loading');
    }
  }

  showNotification(message, type = 'info') {
    this.logger.log(`📢 ${type}: ${message}`);

    const notification = document.createElement('div');
    notification.className = `specifikit-notification specifikit-notification-${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);

    requestAnimationFrame(() => notification.classList.add('specifikit-show'));

    setTimeout(() => {
      notification.classList.remove('specifikit-show');
      notification.addEventListener('transitionend', () => notification.remove());
    }, 4000);
  }

  showConfigError() {
    if (this.container) {
      if (this.container.querySelector('.specifikit-config-error')) return;
      this.container.innerHTML = `
        <div style="background:#fee2e2;border:2px solid #ef4444;padding:30px;margin:40px auto;border-radius:8px;max-width:900px;">
          <h2 style="color:#b91c1c;margin-top:0;">⚠️ JavaScript Customizer Error</h2>
          <p>The customizer failed to initialize. This can happen if the configuration data is incomplete or missing critical values like 'productId'.</p>
          <p><strong>Please check the browser console (F12) for detailed error messages.</strong></p>
        </div>
      `;
    }
  }

  _updateCartUI(cartData) {
    if (cartData && cartData.sections) {
      this.logger.info('✅ Updating UI with new section HTML');
      Object.entries(cartData.sections).forEach(([sectionId, html]) => {
        const sectionElement = document.getElementById(`shopify-section-${sectionId}`) || document.querySelector(`.shopify-section[id*="${sectionId}"]`);
        if (sectionElement && html.trim().length > 0) {
          const newContent = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild;
          if (newContent) {
            sectionElement.innerHTML = newContent.innerHTML;
          }
        }
      });
      const cartDrawer = document.querySelector('cart-drawer, [is="cart-drawer"]');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } else {
      this.updateCartDrawer();
    }
  }

  updateCartDrawer() {
    this.logger.log('🛒 Updating cart drawer (Fallback Event)');
    document.body.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true
    }));

    const cartDrawer = document.querySelector('cart-drawer, [is="cart-drawer"]');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (typeof theme !== 'undefined' && theme.cart?.openDrawer) {
      theme.cart.openDrawer();
    }
  }

  /**
   * Safely escapes a string for insertion into HTML.
   * @param {string} str The string to escape.
   * @returns {string} The escaped string.
   */
  escapeHtml(str) {
    if (!str) return '';
    const p = document.createElement('p');
    p.textContent = str;
    return p.innerHTML;
  }
}

/**
 * ============================================================================
 * END Specifikit UI JS FILE
 * ============================================================================
 */